import type { FC, ReactNode } from 'react'
import type { EnquiryPrefill } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEnquiry } from '@/components/landing/enquiry-context'

interface EnquiryButtonProps {
    prefill: EnquiryPrefill
    label: string
    /** Accessible name when the visible label is generic. */
    ariaLabel?: string
    variant?: 'light' | 'dark' | 'outline-light' | 'outline-dark'
    className?: string
}

const variants = {
    light: 'bg-bone text-charcoal hover:bg-ochre',
    dark: 'bg-charcoal text-bone hover:bg-forest',
    'outline-light': 'border border-bone/30 text-bone hover:border-bone hover:bg-bone hover:text-charcoal',
    'outline-dark': 'border border-charcoal/20 text-charcoal hover:border-charcoal hover:bg-charcoal hover:text-bone'
}

/** Pre-fills the booking form and takes the visitor to it, from any page. */
export const EnquiryButton: FC<EnquiryButtonProps> = ({
    prefill,
    label,
    ariaLabel,
    variant = 'dark',
    className
}): ReactNode => {
    const { requestEnquiry } = useEnquiry()

    return (
        <button
            type='button'
            onClick={() => requestEnquiry(prefill)}
            aria-label={ariaLabel}
            className={cn(
                'inline-flex h-12 items-center gap-3 rounded-full px-6 text-[12px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300',
                variants[variant],
                className
            )}
        >
            {label}
            <ArrowUpRight className='size-4' aria-hidden='true' />
        </button>
    )
}
